// src/pages/Commissions.jsx
// Commission Tracking Module - Beautiful state-of-the-art UI
// Features: Setup Wizard, Statement Inbox, Ledger, Alerts, Disputes, Carrier Metrics

import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, Upload, FileText, AlertTriangle, MessageSquare,
  BarChart3, Settings, ChevronRight, Plus, Search, Filter,
  CheckCircle, XCircle, Clock, TrendingUp, TrendingDown,
  Building2, Users, Wallet, PiggyBank, ArrowUpRight,
  ArrowDownRight, Calendar, RefreshCw, Download, Eye,
  Edit, Trash2, Send, Check, X, AlertCircle, Info,
  FileSpreadsheet, Loader2, ChevronDown, ExternalLink,
  Mail, UserX, ArrowRight, ChevronUp, CircleDot, Percent,
  Image, RotateCcw, Sparkles
} from 'lucide-react';
import { TourBubble } from '../components/tour/TourOverlay';
import { csrfHeadersFor } from '../api';
const API_BASE = process.env.REACT_APP_API_URL || '';


// ============================================================================
// MAIN COMMISSIONS PAGE
// ============================================================================

const TOUR_STEPS = [
  { type: 'splash', title: 'Welcome to Commission Tracking', description: 'Your command center for tracking payments, reconciling statements, and catching missed commissions. Let us show you around.', tab: 'dashboard' },
  { target: 'header', emoji: '🎯', title: 'Your Commission Hub', description: 'Everything lives here — your dashboard, uploaded statements, unpaid clients, and full ledger. One place to track it all.', tab: 'dashboard', position: 'bottom' },
  { target: 'tab-dashboard', emoji: '📊', title: 'Dashboard Overview', description: 'See your YTD earnings, monthly breakdowns by carrier, and payment trends at a glance.', tab: 'dashboard', position: 'bottom' },
  { target: 'statement-coverage', emoji: '✅', title: 'Coverage Tracker', description: 'Green means a carrier statement has been uploaded for this month. Red means it\'s still missing — so you always know what\'s outstanding.', tab: 'dashboard', position: 'top' },
  { target: 'upload-zone', emoji: '📤', title: 'Upload Statements', description: 'Drag and drop your carrier commission statements here — CSV, Excel, or PDF. The system auto-detects the carrier, parses client names, and matches them to your book.', tab: 'statements', position: 'bottom' },
  { target: 'statement-list', emoji: '📋', title: 'Statement History', description: 'Uploaded statements appear here with live status updates. Click any to review parsed details, match clients, and assign a carrier if needed.', tab: 'statements', position: 'top' },
  { target: 'tab-unpaid', emoji: '🔍', title: 'Unpaid Clients', description: 'Generate an unpaid list for any month. Mark each client with a reason — Lost Client, Switched Carriers, Death, Initial Period, or Carrier Error — to clear them.', tab: 'unpaid', position: 'bottom' },
  { target: 'tab-ledger', emoji: '📒', title: 'Commission Ledger', description: 'Your full commission history — every payment, chargeback, and adjustment. Filter by date, status, or type to reconcile.', tab: 'ledger', position: 'bottom' },
  { target: 'upload-zone', emoji: '🚀', title: 'Ready to Launch!', description: 'Drop a carrier statement above to get started! After it processes, click on it to review and assign a carrier if one wasn\'t auto-detected.', tab: 'statements', position: 'bottom' },
  { type: 'celebration', title: 'You\'re All Set!', description: 'You now know everything you need to track and reconcile your commissions like a pro. Upload your first statement to get started.', tab: 'statements' },
];

const Commissions = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tour state
  const showTour = true;
  const [tourStep, setTourStep] = useState(() => {
    if (localStorage.getItem('commissions-tour-dismissed') === 'true') return -1;
    return 0;
  });
  const tourActive = showTour && tourStep >= 0 && tourStep < TOUR_STEPS.length;

  const advanceTour = useCallback(() => {
    const next = tourStep + 1;
    if (next >= TOUR_STEPS.length) {
      setTourStep(-1);
      localStorage.setItem('commissions-tour-dismissed', 'true');
    } else {
      setTourStep(next);
      if (TOUR_STEPS[next].tab) setActiveTab(TOUR_STEPS[next].tab);
    }
  }, [tourStep]);

  const backTour = useCallback(() => {
    const prev = tourStep - 1;
    if (prev >= 0) {
      setTourStep(prev);
      if (TOUR_STEPS[prev].tab) setActiveTab(TOUR_STEPS[prev].tab);
    }
  }, [tourStep]);

  const dismissTour = useCallback(() => {
    setTourStep(-1);
    localStorage.setItem('commissions-tour-dismissed', 'true');
  }, []);

  // Keyboard navigation for tour
  useEffect(() => {
    if (!tourActive) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { dismissTour(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); advanceTour(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); backTour(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [tourActive, advanceTour, backTour, dismissTour]);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/profile`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        setProfile(data.profile);
        // If setup not completed, show wizard
        if (!data.profile?.setup_completed) {
          setActiveTab('setup');
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'statements', label: 'Statements', icon: <Upload className="w-4 h-4" /> },
    { id: 'ledger', label: 'Ledger', icon: <FileText className="w-4 h-4" /> },
    { id: 'unpaid', label: 'Unpaid', icon: <UserX className="w-4 h-4" /> },
    { id: 'alerts', label: 'Alerts', icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'disputes', label: 'Disputes', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'carriers', label: 'Carriers', icon: <Building2 className="w-4 h-4" /> },
    { id: 'setup', label: 'Setup', icon: <Settings className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4" data-tour="header">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl shadow-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Commission Tracking</h1>
                <p className="text-sm text-gray-500">Track, reconcile, and maximize your earnings</p>
              </div>
            </div>
            {profile?.setup_completed && (
              <button
                onClick={() => setActiveTab('statements')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <Upload className="w-4 h-4" />
                Upload Statement
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-px -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-tour={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'dashboard' && <CommissionDashboard profile={profile} />}
        {activeTab === 'statements' && <StatementInbox />}
        {activeTab === 'ledger' && <CommissionLedger />}
        {activeTab === 'unpaid' && <UnpaidClientsTab />}
        {activeTab === 'alerts' && <CommissionAlerts />}
        {activeTab === 'disputes' && <CommissionDisputes />}
        {activeTab === 'carriers' && <CarrierManagement />}
        {activeTab === 'setup' && <SetupWizard profile={profile} onComplete={fetchProfile} />}
      </div>

      {/* Feature Tour */}
      {tourActive && (
        <TourBubble
          stepData={TOUR_STEPS[tourStep]}
          stepIndex={tourStep}
          totalSteps={TOUR_STEPS.length}
          onNext={advanceTour}
          onBack={backTour}
          onDismiss={dismissTour}
          splashIcon={<DollarSign className="w-8 h-8 text-white" />}
          maskId="commission-tour-mask"
        />
      )}
    </div>
  );
};

// ============================================================================
// DASHBOARD
// ============================================================================
const MONTH_NAMES_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const CommissionDashboard = ({ profile }) => {
  const [data, setData] = useState(null);
  const [enhanced, setEnhanced] = useState(null);
  const [carrierCoverage, setCarrierCoverage] = useState(null);
  const [coverageMonth, setCoverageMonth] = useState(new Date().getMonth() + 1);
  const [coverageYear, setCoverageYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
    fetchEnhanced();
    fetchCarrierCoverage(coverageYear, coverageMonth);
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/dashboard`, {
        credentials: 'include'
      });
      const result = await res.json();
      if (result.ok) {
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnhanced = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/dashboard/enhanced`, {
        credentials: 'include'
      });
      const result = await res.json();
      if (result.ok) setEnhanced(result);
    } catch (error) {
      console.error('Error fetching enhanced dashboard:', error);
    }
  };

  const fetchCarrierCoverage = async (year, month) => {
    try {
      const res = await fetch(`${API_BASE}/commissions/dashboard/carrier-coverage?year=${year}&month=${month}`, {
        credentials: 'include'
      });
      const result = await res.json();
      if (result.ok) setCarrierCoverage(result);
    } catch (error) {
      console.error('Error fetching carrier coverage:', error);
    }
  };

  const handleCoverageMonthChange = (month, year) => {
    setCoverageMonth(month);
    setCoverageYear(year);
    fetchCarrierCoverage(year, month);
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!profile?.setup_completed) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Settings className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Complete Your Setup</h2>
        <p className="text-gray-500 mb-6">Set up your commission profile to start tracking your earnings</p>
        <button className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all">
          Start Setup Wizard
        </button>
      </div>
    );
  }

  const ytd = data?.ytd || {};
  const thisMonth = data?.thisMonth || {};
  const alerts = data?.alerts || {};
  const rawMonthlyBreakdown = enhanced?.monthlyBreakdown || [];
  // Fill in all months up to current month so empty months show as $0 bars
  const currentMonth = new Date().getMonth() + 1;
  const monthlyBreakdown = Array.from({ length: currentMonth }, (_, i) => {
    const month = i + 1;
    const existing = rawMonthlyBreakdown.find(m => parseInt(m.month) === month);
    return existing || { month, paid: 0, expected: 0, chargebacks: 0, clients_paid: 0 };
  });
  const maxMonthly = Math.max(...monthlyBreakdown.map(m => parseFloat(m.paid || 0)), 1);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonthName = MONTH_NAMES_FULL[new Date().getMonth()];
  const priorMonthName = MONTH_NAMES_FULL[(new Date().getMonth() + 11) % 12];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="YTD Paid"
          value={formatCurrency(ytd.ytd_paid || 0)}
          icon={<Wallet className="w-5 h-5" />}
          color="green"
          trend={ytd.ytd_paid > (ytd.ytd_expected * 0.9) ? 'up' : 'down'}
        />
        <MetricCard
          title="YTD Expected"
          value={formatCurrency(ytd.ytd_expected || 0)}
          icon={<PiggyBank className="w-5 h-5" />}
          color="blue"
        />
        <MetricCard
          title="YTD Chargebacks"
          value={formatCurrency(ytd.ytd_chargebacks || 0)}
          icon={<ArrowDownRight className="w-5 h-5" />}
          color="red"
        />
        <MetricCard
          title="Missing Payments"
          value={formatCurrency(ytd.ytd_missing || 0)}
          icon={<AlertCircle className="w-5 h-5" />}
          color="amber"
          alert={parseFloat(ytd.ytd_missing) > 0}
        />
      </div>

      {/* Current Month + Prior Month + Coverage Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">{currentMonthName} Paid</h3>
            <Calendar className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(thisMonth.month_paid || 0)}</div>
          <div className="text-xs text-gray-500 mt-1">Expected: {formatCurrency(thisMonth.month_expected || 0)}</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">{currentMonthName} — Clients Paid</h3>
            <Users className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900">{enhanced?.clientsPaidThisMonth || 0}</span>
            <span className="text-sm text-gray-500">of {enhanced?.totalClients || 0}</span>
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all"
              style={{ width: `${enhanced?.totalClients ? Math.min(100, Math.round((enhanced.clientsPaidThisMonth / enhanced.totalClients) * 100)) : 0}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">{priorMonthName} — Clients Paid</h3>
            <Users className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900">{enhanced?.clientsPaidPriorMonth || 0}</span>
            <span className="text-sm text-gray-500">of {enhanced?.totalClients || 0}</span>
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full transition-all"
              style={{ width: `${enhanced?.totalClients ? Math.min(100, Math.round(((enhanced?.clientsPaidPriorMonth || 0) / enhanced.totalClients) * 100)) : 0}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">{formatCurrency(enhanced?.priorMonthPaid || 0)} paid</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Alerts &amp; Disputes</h3>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className={`text-xl font-bold ${(alerts.critical_count || 0) + (alerts.new_count || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {(alerts.critical_count || 0) + (alerts.new_count || 0)}
              </div>
              <div className="text-xs text-gray-500">Alerts</div>
            </div>
            <div className="text-center">
              <div className={`text-xl font-bold ${(data?.disputes?.open_disputes || 0) > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                {data?.disputes?.open_disputes || 0}
              </div>
              <div className="text-xs text-gray-500">Disputes</div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Chart */}
      {monthlyBreakdown.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Monthly Commissions ({new Date().getFullYear()})</h3>
          <div className="flex items-end gap-2 h-48">
            {monthlyBreakdown.map((m, idx) => {
              const paid = parseFloat(m.paid || 0);
              const height = paid > 0 && maxMonthly > 0 ? Math.max(8, (paid / maxMonthly) * 100) : 2;
              const isCurrent = parseInt(m.month) === new Date().getMonth() + 1;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-8 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                    {formatCurrency(paid)}
                  </div>
                  <div
                    className={`w-full rounded-t-md transition-all ${paid === 0 ? 'bg-gray-200' : isCurrent ? 'bg-gradient-to-t from-amber-500 to-amber-400' : 'bg-gradient-to-t from-green-500 to-green-400'} hover:opacity-80`}
                    style={{ height: `${height}%`, minHeight: '4px' }}
                  />
                  <span className={`text-xs ${isCurrent ? 'font-bold text-amber-600' : 'text-gray-500'}`}>
                    {monthNames[parseInt(m.month) - 1]}
                  </span>
                  {paid > 0 && (
                    <span className="text-[10px] text-gray-400 font-medium">{formatCurrency(paid)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Statement Coverage by Carrier */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" data-tour="statement-coverage">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Statement Coverage</h3>
          <select
            value={`${coverageYear}-${coverageMonth}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split('-').map(Number);
              handleCoverageMonthChange(m, y);
            }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            {Array.from({ length: 12 }, (_, i) => {
              const d = new Date();
              d.setDate(1);
              d.setMonth(d.getMonth() - i);
              return { y: d.getFullYear(), m: d.getMonth() + 1 };
            }).map(({ y, m }) => (
              <option key={`${y}-${m}`} value={`${y}-${m}`}>
                {MONTH_NAMES_FULL[m - 1]} {y}
              </option>
            ))}
          </select>
        </div>
        {!carrierCoverage || carrierCoverage.carriers?.length === 0 ? (
          <p className="text-gray-400 text-center py-4">No carriers found in client list</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {carrierCoverage.carriers.map((c, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  c.hasStatement
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {c.hasStatement ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                {c.name} ({c.clientCount})
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Carrier Breakdown & Recent Statements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Carrier Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">YTD by Carrier</h3>
          <div className="space-y-3">
            {(data?.carrierBreakdown || []).length === 0 ? (
              <p className="text-gray-400 text-center py-4">No commission data yet</p>
            ) : (
              data.carrierBreakdown.map((carrier, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium text-gray-700">{carrier.carrier}</span>
                  </div>
                  <span className="font-semibold text-green-600">{formatCurrency(carrier.paid)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Statements */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Recent Statements</h3>
          <div className="space-y-3">
            {(data?.recentStatements || []).length === 0 ? (
              <p className="text-gray-400 text-center py-4">No statements uploaded yet</p>
            ) : (
              data.recentStatements.map((stmt) => (
                <div key={stmt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-gray-700 text-sm truncate max-w-[200px]">
                        {stmt.original_filename}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(stmt.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={stmt.status} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SETUP WIZARD
// ============================================================================
const SetupWizard = ({ profile, onComplete }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    isIndependentBroker: profile?.is_independent_broker ?? true,
    commissionType: profile?.commission_type || 'direct',
    isLoaAgent: profile?.is_loa_agent || false,
    loaPercentage: profile?.loa_percentage || 100,
    uplineName: profile?.upline_name || '',
  });
  const [saving, setSaving] = useState(false);

  // Direct: step 0 only. LOA: step 0 + step 1 (LOA details)
  const isLoa = formData.commissionType === 'loa';
  const totalSteps = isLoa ? 2 : 1;
  const isLastStep = isLoa ? step === 1 : step === 0;

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/commissions/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          isLoaAgent: isLoa,
          setupCompleted: isLastStep
        })
      });
      const data = await res.json();
      if (data.ok && isLastStep) {
        onComplete();
      } else if (!data.ok) {
        alert(data.error || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!isLastStep) {
      await saveProfile();
      setStep(step + 1);
    } else {
      await saveProfile();
    }
  };

  const progress = ((step + 1) / totalSteps) * 100;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Progress Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6">
          <h2 className="text-xl font-bold text-white mb-2">Commission Profile Setup</h2>
          <p className="text-amber-100 text-sm mb-4">Step {step + 1} of {totalSteps}</p>
          <div className="h-2 bg-amber-400/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6">
          {/* Step 0: Agent Type Selection */}
          {step === 0 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Let's get started!</h3>
                <p className="text-gray-500">Tell us about your business model. Carriers and rates will be detected automatically from your uploaded statements.</p>
              </div>

              {/* Independent Broker Checkbox */}
              <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isIndependentBroker}
                    onChange={(e) => setFormData({ ...formData, isIndependentBroker: e.target.checked })}
                    className="mt-1 w-6 h-6 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-lg font-semibold text-gray-900">I am an Independent Broker</span>
                    <p className="text-sm text-gray-600 mt-1">
                      You operate your own book of business and contract directly or through an FMO/upline.
                    </p>
                  </div>
                </label>
              </div>

              {/* Commission Type Selection */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  How do you receive your commissions?
                </label>

                <div
                  onClick={() => setFormData({ ...formData, commissionType: 'direct' })}
                  className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.commissionType === 'direct'
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      formData.commissionType === 'direct' ? 'border-amber-500' : 'border-gray-300'
                    }`}>
                      {formData.commissionType === 'direct' && (
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                      )}
                    </div>
                    <div>
                      <span className="text-lg font-semibold text-gray-900">Direct / Street Level</span>
                      <p className="text-sm text-gray-600 mt-1">
                        You receive full CMS commission rates directly from carriers.
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-sm text-green-600 font-medium">
                        <CheckCircle className="w-4 h-4" />
                        100% of CMS rates
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  onClick={() => setFormData({ ...formData, commissionType: 'loa' })}
                  className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.commissionType === 'loa'
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      formData.commissionType === 'loa' ? 'border-amber-500' : 'border-gray-300'
                    }`}>
                      {formData.commissionType === 'loa' && (
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                      )}
                    </div>
                    <div>
                      <span className="text-lg font-semibold text-gray-900">LOA / Non-Street Level</span>
                      <p className="text-sm text-gray-600 mt-1">
                        You receive a percentage of commissions through an upline, FMO, or agency.
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-sm text-amber-600 font-medium">
                        <Users className="w-4 h-4" />
                        Percentage of CMS rates through upline
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  You don't need to add carriers or rates manually. When you upload commission statements, the system will automatically detect carriers, parse payments, and match them to your clients.
                </p>
              </div>
            </div>
          )}

          {/* Step 1: LOA Details (only for LOA agents) */}
          {step === 1 && isLoa && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">LOA Commission Details</h3>
              <p className="text-gray-500">Enter the details of your commission arrangement with your upline.</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upline / FMO Name
                </label>
                <input
                  type="text"
                  value={formData.uplineName}
                  onChange={(e) => setFormData({ ...formData, uplineName: e.target.value })}
                  placeholder="e.g., Senior Marketing Specialists"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              <div className="p-5 bg-blue-50 rounded-xl">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Your Commission Percentage
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  What percentage of the street-level commission do you receive?
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.loaPercentage}
                    onChange={(e) => setFormData({ ...formData, loaPercentage: parseFloat(e.target.value) || 0 })}
                    className="w-32 px-4 py-3 text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-center"
                  />
                  <span className="text-xl font-medium text-gray-500">%</span>
                </div>

                {formData.loaPercentage > 0 && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Example:</span> If CMS rate is $600, you would receive{' '}
                      <span className="font-bold">${((600 * formData.loaPercentage) / 100).toFixed(2)}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={() => setStep(step - 1)}
            disabled={step === 0}
            className={`px-4 py-2 rounded-lg font-medium ${
              step === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isLastStep ? (
              'Complete Setup'
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STATEMENT INBOX
// ============================================================================
const StatementInbox = () => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = React.useRef(0);
  const fileInputRef = React.useRef(null);
  const [selectedStatement, setSelectedStatement] = useState(null);

  // Prevent browser from opening dropped files anywhere on the page
  useEffect(() => {
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  useEffect(() => {
    fetchStatements();
  }, []);

  const fetchStatements = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/statements`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) setStatements(data.statements);
    } catch (error) {
      console.error('Error fetching statements:', error);
    } finally {
      setLoading(false);
    }
  };

  const allowedExts = ['.csv', '.xlsx', '.xls', '.pdf', '.jpg', '.jpeg', '.png', '.heic', '.webp'];

  const uploadMultipleFiles = async (files) => {
    const fileList = Array.from(files).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return allowedExts.includes(ext);
    });
    if (fileList.length === 0) return;

    setUploading(true);
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      setUploadProgress(`Uploading ${i + 1} of ${fileList.length}: ${file.name}`);
      const formData = new FormData();
      formData.append('file', file);
      try {
        await fetch(`${API_BASE}/commissions/statements/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
      }
    }
    setUploading(false);
    setUploadProgress('');
    fetchStatements();
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await uploadMultipleFiles(files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadMultipleFiles(files);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-tour="upload-zone">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.heic,.webp"
          multiple
          onChange={handleUpload}
          className="hidden"
          disabled={uploading}
        />
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer select-none ${
            isDragging ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-amber-400'
          }`}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {uploading ? (
            <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-spin" />
          ) : isDragging ? (
            <Download className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          ) : (
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          )}
          <p className="text-lg font-medium text-gray-700 mb-1">
            {uploading ? uploadProgress || 'Uploading...' : isDragging ? 'Drop files here' : 'Drop your commission statements here'}
          </p>
          <p className="text-sm text-gray-500">
            Supports CSV, Excel, PDF, and images (JPG, PNG) up to 20MB each. Upload multiple files at once.
          </p>
        </div>
      </div>

      {/* Statements List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-tour="statement-list">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Uploaded Statements</h3>
        </div>

        {statements.length === 0 ? (
          <div className="p-8 text-center">
            <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No statements uploaded yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {statements.map(stmt => (
              <div
                key={stmt.id}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setSelectedStatement(stmt)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileSpreadsheet className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{stmt.original_filename}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{new Date(stmt.created_at).toLocaleDateString()}</span>
                        {(stmt.carrier_name || stmt.detected_carrier || stmt.ai_detected_carrier) && <span>• {stmt.carrier_name || stmt.detected_carrier || stmt.ai_detected_carrier}</span>}
                        {stmt.total_rows > 0 && <span>• {stmt.total_rows} rows</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={stmt.status} />
                    {stmt.total_commission_amount > 0 && (
                      <span className="font-semibold text-green-600">
                        {formatCurrency(stmt.total_commission_amount)}
                      </span>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Statement Detail Modal */}
      {selectedStatement && (
        <StatementDetailModal
          statement={selectedStatement}
          onClose={() => setSelectedStatement(null)}
          onUpdate={fetchStatements}
        />
      )}
    </div>
  );
};

// ============================================================================
// COMMISSION LEDGER
// ============================================================================
const CommissionLedger = () => {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    entryType: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchLedger();
  }, [filters]);

  const fetchLedger = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.entryType) params.append('entryType', filters.entryType);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const res = await fetch(`${API_BASE}/commissions/ledger?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        setEntries(data.entries);
        setTotals(data.totals);
      }
    } catch (error) {
      console.error('Error fetching ledger:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-6">
      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">Total Expected</p>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(totals.total_expected || 0)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">Total Paid</p>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.total_paid || 0)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">Total Chargebacks</p>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.total_chargebacks || 0)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">Total Entries</p>
          <p className="text-2xl font-bold text-gray-900">{totals.total_entries || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="variance">Variance</option>
            <option value="missing">Missing</option>
            <option value="disputed">Disputed</option>
          </select>

          <select
            value={filters.entryType}
            onChange={(e) => setFilters({ ...filters, entryType: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="expected">Expected</option>
            <option value="paid">Paid</option>
            <option value="chargeback">Chargeback</option>
            <option value="adjustment">Adjustment</option>
          </select>

          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Start Date"
          />

          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="End Date"
          />

          <button
            onClick={() => setFilters({ status: '', entryType: '', startDate: '', endDate: '' })}
            className="px-3 py-2 text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Carrier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No ledger entries found
                  </td>
                </tr>
              ) : (
                entries.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {entry.effective_date ? new Date(entry.effective_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {entry.clientName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {entry.carrier_display_name || entry.carrier_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        entry.commission_type === 'initial' ? 'bg-green-100 text-green-700' :
                        entry.commission_type === 'renewal' ? 'bg-blue-100 text-blue-700' :
                        entry.commission_type === 'chargeback' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {entry.commission_type}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-sm font-semibold text-right ${
                      entry.entry_type === 'chargeback' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {entry.entry_type === 'chargeback' ? '-' : ''}{formatCurrency(entry.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={entry.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ALERTS
// ============================================================================
const CommissionAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');

  useEffect(() => {
    fetchAlerts();
  }, [statusFilter]);

  const fetchAlerts = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const res = await fetch(`${API_BASE}/commissions/alerts?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        setAlerts(data.alerts);
        setCounts(data.counts);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAlert = async (alertId, status) => {
    try {
      await fetch(`${API_BASE}/commissions/alerts/${alertId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      fetchAlerts();
    } catch (error) {
      console.error('Error updating alert:', error);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-6">
      {/* Alert Counts */}
      <div className="flex gap-2">
        <button
          onClick={() => setStatusFilter('new')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            statusFilter === 'new'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          New ({counts.new_count || 0})
        </button>
        <button
          onClick={() => setStatusFilter('')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            statusFilter === ''
              ? 'bg-amber-100 text-amber-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setStatusFilter('resolved')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            statusFilter === 'resolved'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Resolved
        </button>
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {alerts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No alerts</p>
            <p className="text-sm text-gray-400">All commission payments are on track</p>
          </div>
        ) : (
          alerts.map(alert => (
            <div
              key={alert.id}
              className={`bg-white rounded-xl shadow-sm border overflow-hidden ${
                alert.severity === 'critical' ? 'border-red-300' :
                alert.severity === 'warning' ? 'border-amber-300' :
                'border-gray-200'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      alert.severity === 'critical' ? 'bg-red-100' :
                      alert.severity === 'warning' ? 'bg-amber-100' :
                      'bg-blue-100'
                    }`}>
                      <AlertTriangle className={`w-5 h-5 ${
                        alert.severity === 'critical' ? 'text-red-600' :
                        alert.severity === 'warning' ? 'text-amber-600' :
                        'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                      {alert.description && (
                        <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        {alert.clientName && <span>{alert.clientName}</span>}
                        {alert.carrier_display_name && <span>• {alert.carrier_display_name}</span>}
                        <span>• {new Date(alert.created_at).toLocaleDateString()}</span>
                      </div>
                      {(alert.expected_amount || alert.actual_amount) && (
                        <div className="flex gap-4 mt-2 text-sm">
                          {alert.expected_amount && (
                            <span className="text-gray-600">
                              Expected: <span className="font-medium">{formatCurrency(alert.expected_amount)}</span>
                            </span>
                          )}
                          {alert.actual_amount && (
                            <span className="text-gray-600">
                              Actual: <span className="font-medium">{formatCurrency(alert.actual_amount)}</span>
                            </span>
                          )}
                          {alert.variance_amount && (
                            <span className={`font-medium ${alert.variance_amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              Variance: {formatCurrency(alert.variance_amount)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {alert.status === 'new' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateAlert(alert.id, 'acknowledged')}
                        className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Acknowledge"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => updateAlert(alert.id, 'resolved')}
                        className="p-2 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                        title="Resolve"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => updateAlert(alert.id, 'dismissed')}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============================================================================
// DISPUTES
// ============================================================================
const CommissionDisputes = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDispute, setShowNewDispute] = useState(false);

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/disputes`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) setDisputes(data.disputes);
    } catch (error) {
      console.error('Error fetching disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Commission Disputes</h3>
        <button
          onClick={() => setShowNewDispute(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
        >
          <Plus className="w-4 h-4" />
          New Dispute
        </button>
      </div>

      {/* Disputes List */}
      <div className="space-y-3">
        {disputes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No disputes</p>
            <p className="text-sm text-gray-400">Create a dispute to track commission issues with carriers</p>
          </div>
        ) : (
          disputes.map(dispute => (
            <div key={dispute.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">{dispute.title}</h4>
                  {dispute.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{dispute.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                    {dispute.clientName && <span>{dispute.clientName}</span>}
                    {dispute.carrier_display_name && <span>• {dispute.carrier_display_name}</span>}
                    <span>• Opened {new Date(dispute.created_at).toLocaleDateString()}</span>
                  </div>
                  {dispute.disputed_amount && (
                    <p className="mt-2 text-sm font-semibold text-amber-600">
                      Disputed: {formatCurrency(dispute.disputed_amount)}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusBadge status={dispute.status} />
                  {dispute.case_number && (
                    <span className="text-xs text-gray-500">Case: {dispute.case_number}</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* New Dispute Modal */}
      {showNewDispute && (
        <NewDisputeModal
          onClose={() => setShowNewDispute(false)}
          onCreated={() => {
            setShowNewDispute(false);
            fetchDisputes();
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// CARRIER MANAGEMENT
// ============================================================================
const CarrierManagement = () => {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([]);

  useEffect(() => {
    fetchCarriers();
    fetchMetrics();
  }, []);

  const fetchCarriers = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/carriers`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) setCarriers(data.carriers);
    } catch (error) {
      console.error('Error fetching carriers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/carrier-metrics`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) setMetrics(data.metrics);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-6">
      {/* Carrier Performance */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Carrier Performance (YTD)</h3>
        {metrics.length === 0 ? (
          <p className="text-gray-400 text-center py-4">No performance data yet</p>
        ) : (
          <div className="space-y-4">
            {metrics.map((m, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{m.carrier}</p>
                    <p className="text-sm text-gray-500">{m.client_count} clients</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-right">
                    <p className="text-gray-500">Paid</p>
                    <p className="font-semibold text-green-600">{formatCurrency(m.total_paid)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500">Accuracy</p>
                    <p className="font-semibold">{m.paymentAccuracy}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500">Chargebacks</p>
                    <p className="font-semibold text-red-600">{m.chargebackRate}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Carrier List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Your Carriers</h3>
          <AddCarrierButton onAdd={fetchCarriers} />
        </div>

        {carriers.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No carriers configured</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {carriers.map(carrier => (
              <CarrierCard key={carrier.id} carrier={carrier} onUpdate={fetchCarriers} expanded />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// UNPAID CLIENTS TAB
// ============================================================================
const RESOLUTION_REASONS = [
  { key: 'payment_received', label: 'Paid', color: 'bg-green-100 text-green-700 hover:bg-green-200', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  { key: 'lost_client', label: 'Lost Client', color: 'bg-red-100 text-red-700 hover:bg-red-200', icon: <UserX className="w-3.5 h-3.5" /> },
  { key: 'switched_carriers', label: 'Switched Carriers', color: 'bg-orange-100 text-orange-700 hover:bg-orange-200', icon: <ArrowRight className="w-3.5 h-3.5" /> },
  { key: 'death', label: 'Death', color: 'bg-gray-100 text-gray-600 hover:bg-gray-200', icon: <XCircle className="w-3.5 h-3.5" /> },
  { key: 'initial_period', label: 'Initial Period', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200', icon: <Clock className="w-3.5 h-3.5" /> },
  { key: 'carrier_error', label: 'Carrier Error', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200', icon: <AlertCircle className="w-3.5 h-3.5" /> },
  { key: 'other', label: 'Other', color: 'bg-slate-100 text-slate-700 hover:bg-slate-200', icon: <Edit className="w-3.5 h-3.5" /> },
];

const REASON_BADGE_COLORS = {
  payment_received: 'bg-green-100 text-green-700',
  lost_client: 'bg-red-100 text-red-700',
  switched_carriers: 'bg-orange-100 text-orange-700',
  death: 'bg-gray-200 text-gray-600',
  initial_period: 'bg-purple-100 text-purple-700',
  carrier_error: 'bg-blue-100 text-blue-700',
  other: 'bg-slate-100 text-slate-700',
};

const UnpaidClientsTab = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [clients, setClients] = useState([]);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [otherNotesId, setOtherNotesId] = useState(null);
  const [otherNotesText, setOtherNotesText] = useState('');

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    fetchUnpaid();
  }, [year, month, showResolved]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdownId) return;
    const handler = () => setOpenDropdownId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openDropdownId]);

  const fetchUnpaid = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/commissions/unpaid?year=${year}&month=${month}&hideResolved=${!showResolved}`, {
        credentials: 'include'
      });
      const result = await res.json();
      if (result.ok) {
        setClients(result.clients || []);
        setResolvedCount(result.resolvedCount || 0);
      }
    } catch (error) {
      console.error('Error fetching unpaid clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_BASE}/commissions/unpaid/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ year, month })
      });
      const result = await res.json();
      if (result.ok) {
        fetchUnpaid();
      }
    } catch (error) {
      console.error('Error generating unpaid list:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleResolve = async (id, reason, notes) => {
    setUpdatingId(id);
    try {
      const body = {
        pipeline_status: reason === 'payment_received' ? 'payment_received' : 'written_off',
        resolution_reason: reason,
      };
      if (notes) body.resolution_notes = notes;
      const res = await fetch(`${API_BASE}/commissions/unpaid/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        setOpenDropdownId(null);
        setOtherNotesId(null);
        setOtherNotesText('');
        fetchUnpaid();
      }
    } catch (error) {
      console.error('Error resolving unpaid client:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleUndo = async (id) => {
    setUpdatingId(id);
    try {
      const res = await fetch(`${API_BASE}/commissions/unpaid/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipeline_status: 'identified' })
      });
      if (res.ok) fetchUnpaid();
    } catch (error) {
      console.error('Error undoing resolution:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  // Split clients into active vs resolved
  const resolvedStatuses = ['payment_received', 'written_off'];
  const activeClients = clients.filter(c => !resolvedStatuses.includes(c.pipeline_status));
  const resolvedClients = clients.filter(c => resolvedStatuses.includes(c.pipeline_status));

  return (
    <div className="space-y-6">
      {/* Period Selector & Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-900">Unpaid Clients Workspace</h3>
            <div className="flex items-center gap-2">
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
              >
                {monthNames.map((name, idx) => (
                  <option key={idx} value={idx + 1}>{name}</option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
              >
                {[now.getFullYear(), now.getFullYear() - 1].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {generating ? 'Generating...' : 'Generate Unpaid List'}
            </button>
            {clients.length > 0 && (
              <button
                onClick={() => setEmailModal({ step: 'choose' })}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <Mail className="w-4 h-4" />
                Email About Missing Payments
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Bar */}
      {(activeClients.length > 0 || resolvedCount > 0) && (
        <div className="flex items-center gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-3 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-semibold text-gray-900">{activeClients.length} Unresolved</span>
          </div>
          {resolvedCount > 0 && (
            <button
              onClick={() => setShowResolved(!showResolved)}
              className={`rounded-xl shadow-sm border px-5 py-3 flex items-center gap-3 transition-colors ${
                showResolved ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <CheckCircle className={`w-5 h-5 ${showResolved ? 'text-green-500' : 'text-gray-400'}`} />
              <span className="text-sm font-semibold text-gray-700">
                {showResolved ? 'Hide' : 'Show'} Resolved ({resolvedCount})
              </span>
            </button>
          )}
        </div>
      )}

      {/* Client List */}
      {loading ? (
        <LoadingState />
      ) : clients.length === 0 && resolvedCount === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <UserX className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Unpaid Clients</h3>
          <p className="text-gray-500 mb-4">
            Click "Generate Unpaid List" to compare your client book against commission payments for {monthNames[month - 1]} {year}.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active (unresolved) clients */}
          {activeClients.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-700">{activeClients.length} unpaid client{activeClients.length !== 1 ? 's' : ''} — {monthNames[month - 1]} {year}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {activeClients.map(client => {
                  const isUpdating = updatingId === client.id;
                  const isDropdownOpen = openDropdownId === client.id;
                  const isOtherNotes = otherNotesId === client.id;
                  return (
                    <div key={client.id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">{client.clientName || 'Unknown Client'}</span>
                            {(client.carrier || client.commission_carrier_name) && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{client.carrier || client.commission_carrier_name}</span>
                            )}
                            {client.commission_type && (
                              <span className="text-xs text-gray-500">{client.commission_type}</span>
                            )}
                          </div>
                          {client.expected_amount && (
                            <div className="text-sm text-gray-500 mt-0.5">Expected: {formatCurrency(client.expected_amount)}</div>
                          )}
                          {client.email_sent_at && (
                            <div className="text-xs text-blue-600 mt-0.5">
                              Email sent {new Date(client.email_sent_at).toLocaleDateString()}
                              {client.email_sent_to ? ` to ${client.email_sent_to}` : ''}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0 relative">
                          {isUpdating ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          ) : (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); setOpenDropdownId(isDropdownOpen ? null : client.id); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
                              >
                                Resolve
                                <ChevronDown className={`w-3 h-3 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                              </button>
                              {isDropdownOpen && (
                                <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-48" onClick={(e) => e.stopPropagation()}>
                                  {RESOLUTION_REASONS.map(reason => (
                                    <button
                                      key={reason.key}
                                      onClick={() => {
                                        if (reason.key === 'other') {
                                          setOtherNotesId(client.id);
                                          setOpenDropdownId(null);
                                        } else {
                                          handleResolve(client.id, reason.key);
                                        }
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors"
                                    >
                                      {reason.icon}
                                      {reason.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Other notes inline input */}
                      {isOtherNotes && (
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="text"
                            value={otherNotesText}
                            onChange={(e) => setOtherNotesText(e.target.value)}
                            placeholder="Enter reason..."
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && otherNotesText.trim()) {
                                handleResolve(client.id, 'other', otherNotesText.trim());
                              } else if (e.key === 'Escape') {
                                setOtherNotesId(null);
                                setOtherNotesText('');
                              }
                            }}
                          />
                          <button
                            onClick={() => {
                              if (otherNotesText.trim()) handleResolve(client.id, 'other', otherNotesText.trim());
                            }}
                            className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => { setOtherNotesId(null); setOtherNotesText(''); }}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resolved clients (shown when toggled) */}
          {showResolved && resolvedClients.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden opacity-80">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <span className="text-sm font-medium text-gray-500">Resolved ({resolvedClients.length})</span>
              </div>
              <div className="divide-y divide-gray-100">
                {resolvedClients.map(client => {
                  const isUpdating = updatingId === client.id;
                  const reasonInfo = RESOLUTION_REASONS.find(r => r.key === client.resolution_reason);
                  const badgeColor = REASON_BADGE_COLORS[client.resolution_reason] || 'bg-gray-100 text-gray-600';
                  return (
                    <div key={client.id} className="px-5 py-3 flex items-center justify-between gap-4 bg-gray-50/50">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-500">{client.clientName || 'Unknown Client'}</span>
                          {(client.carrier || client.commission_carrier_name) && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-500">{client.carrier || client.commission_carrier_name}</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeColor}`}>
                            {reasonInfo?.label || (client.pipeline_status === 'payment_received' ? 'Paid' : 'Written Off')}
                          </span>
                        </div>
                        {client.resolution_notes && (
                          <div className="text-xs text-gray-400 mt-0.5 italic">{client.resolution_notes}</div>
                        )}
                        {client.resolved_at && (
                          <div className="text-xs text-gray-400 mt-0.5">Resolved {new Date(client.resolved_at).toLocaleDateString()}</div>
                        )}
                      </div>
                      <button
                        onClick={() => handleUndo(client.id)}
                        disabled={isUpdating}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                        title="Undo — move back to unresolved"
                      >
                        {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Undo
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <UnpaidEmailModal
          year={year}
          month={month}
          monthName={monthNames[month - 1]}
          clients={clients}
          emailModal={emailModal}
          setEmailModal={setEmailModal}
          onSent={fetchUnpaid}
        />
      )}
    </div>
  );
};

// ============================================================================
// UNPAID EMAIL MODAL
// ============================================================================
const UnpaidEmailModal = ({ year, month, monthName, clients, emailModal, setEmailModal, onSent }) => {
  const [targetType, setTargetType] = useState('carrier');
  const [carrierId, setCarrierId] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [draft, setDraft] = useState(null);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  // Get unique carriers from unpaid list
  const carriers = [];
  const carrierMap = {};
  clients.forEach(c => {
    if (c.carrier_name && !carrierMap[c.carrier_name]) {
      carrierMap[c.carrier_name] = true;
      carriers.push({ id: c.carrier_id, name: c.carrier_name });
    }
  });

  const handleGenerateEmail = async () => {
    setGeneratingEmail(true);
    setError(null);
    try {
      const body = { targetType, year, month };
      if (targetType === 'carrier' && carrierId) body.carrierId = carrierId;
      const res = await fetch(`${API_BASE}/commissions/unpaid/generate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const result = await res.json();
      if (result.ok) {
        setDraft(result);
        setEmailModal({ step: 'preview' });
      } else {
        setError(result.error || 'Failed to generate email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingEmail(false);
    }
  };

  const handleSend = async () => {
    if (!recipientEmail.trim()) { setError('Please enter recipient email'); return; }
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/commissions/unpaid/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          emailId: draft.email.id,
          recipientEmail: recipientEmail.trim(),
          subject: draft.subject,
          body: draft.body
        })
      });
      const result = await res.json();
      if (result.ok) {
        setSent(true);
        setTimeout(() => { setEmailModal(null); onSent(); }, 2000);
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Email About Missing Payments</h3>
          </div>
          <button onClick={() => setEmailModal(null)} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {sent ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Sent!</h3>
              <p className="text-gray-500">The email has been sent and pipeline statuses updated.</p>
            </div>
          ) : emailModal.step === 'choose' ? (
            <div className="space-y-6">
              <p className="text-gray-600">
                Generate an AI-powered email about <strong>{clients.filter(c => c.pipeline_status !== 'payment_received' && c.pipeline_status !== 'written_off').length} unpaid clients</strong> for {monthName} {year}.
              </p>

              {/* Target Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Who do you want to email?</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setTargetType('carrier')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      targetType === 'carrier'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Building2 className={`w-6 h-6 mb-2 ${targetType === 'carrier' ? 'text-amber-600' : 'text-gray-400'}`} />
                    <div className="font-semibold text-gray-900">A Specific Carrier</div>
                    <div className="text-sm text-gray-500 mt-1">Only include that carrier's unpaid clients</div>
                  </button>
                  <button
                    onClick={() => { setTargetType('upline'); setCarrierId(''); }}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      targetType === 'upline'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className={`w-6 h-6 mb-2 ${targetType === 'upline' ? 'text-amber-600' : 'text-gray-400'}`} />
                    <div className="font-semibold text-gray-900">My Upline</div>
                    <div className="text-sm text-gray-500 mt-1">Include all unpaid clients across carriers</div>
                  </button>
                </div>
              </div>

              {/* Carrier Selection */}
              {targetType === 'carrier' && carriers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Carrier</label>
                  <select
                    value={carrierId}
                    onChange={(e) => setCarrierId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">All carriers</option>
                    {carriers.map(c => (
                      <option key={c.id || c.name} value={c.id || ''}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
              )}
            </div>
          ) : emailModal.step === 'preview' && draft ? (
            <div className="space-y-4">
              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="Enter recipient email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={draft.subject || ''}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Body</label>
                <textarea
                  value={draft.body || ''}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                />
              </div>

              {draft.clientCount && (
                <p className="text-xs text-gray-500">
                  This email references {draft.clientCount} unpaid client{draft.clientCount !== 1 ? 's' : ''}.
                </p>
              )}

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {!sent && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button onClick={() => setEmailModal(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            {emailModal.step === 'choose' ? (
              <button
                onClick={handleGenerateEmail}
                disabled={generatingEmail}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg disabled:opacity-50"
              >
                {generatingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {generatingEmail ? 'Generating...' : 'Generate Email'}
              </button>
            ) : emailModal.step === 'preview' ? (
              <button
                onClick={handleSend}
                disabled={sending || !recipientEmail.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const MetricCard = ({ title, value, icon, color, trend, alert }) => {
  const colorClasses = {
    green: 'from-green-500 to-green-600',
    blue: 'from-blue-500 to-blue-600',
    red: 'from-red-500 to-red-600',
    amber: 'from-amber-500 to-amber-600',
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border ${alert ? 'border-amber-300' : 'border-gray-200'} p-5`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 bg-gradient-to-br ${colorClasses[color]} rounded-lg text-white`}>
          {icon}
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{title}</p>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    uploaded: { color: 'bg-gray-100 text-gray-700', label: 'Uploaded' },
    parsing: { color: 'bg-blue-100 text-blue-700', label: 'Parsing' },
    parsed: { color: 'bg-blue-100 text-blue-700', label: 'Parsed' },
    matching: { color: 'bg-amber-100 text-amber-700', label: 'Matching' },
    matched: { color: 'bg-amber-100 text-amber-700', label: 'Matched' },
    review_needed: { color: 'bg-red-100 text-red-700', label: 'Review Needed' },
    completed: { color: 'bg-green-100 text-green-700', label: 'Completed' },
    failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
    pending: { color: 'bg-gray-100 text-gray-700', label: 'Pending' },
    paid: { color: 'bg-green-100 text-green-700', label: 'Paid' },
    variance: { color: 'bg-amber-100 text-amber-700', label: 'Variance' },
    missing: { color: 'bg-red-100 text-red-700', label: 'Missing' },
    disputed: { color: 'bg-purple-100 text-purple-700', label: 'Disputed' },
    new: { color: 'bg-blue-100 text-blue-700', label: 'New' },
    acknowledged: { color: 'bg-gray-100 text-gray-700', label: 'Acknowledged' },
    investigating: { color: 'bg-amber-100 text-amber-700', label: 'Investigating' },
    resolved: { color: 'bg-green-100 text-green-700', label: 'Resolved' },
    dismissed: { color: 'bg-gray-100 text-gray-700', label: 'Dismissed' },
    open: { color: 'bg-blue-100 text-blue-700', label: 'Open' },
    submitted: { color: 'bg-amber-100 text-amber-700', label: 'Submitted' },
    pending_response: { color: 'bg-amber-100 text-amber-700', label: 'Pending Response' },
    in_review: { color: 'bg-purple-100 text-purple-700', label: 'In Review' },
    escalated: { color: 'bg-red-100 text-red-700', label: 'Escalated' },
    resolved_won: { color: 'bg-green-100 text-green-700', label: 'Won' },
    resolved_lost: { color: 'bg-gray-100 text-gray-700', label: 'Lost' },
    withdrawn: { color: 'bg-gray-100 text-gray-700', label: 'Withdrawn' },
  };

  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', label: status };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  );
};

const LoadingState = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
  </div>
);

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(num);
};

// ============================================================================
// MODAL COMPONENTS (Simplified versions)
// ============================================================================

const AddCarrierButton = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/commissions/carriers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });
      if (res.ok) {
        setOpen(false);
        setName('');
        onAdd();
      }
    } catch (error) {
      console.error('Error adding carrier:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-700 font-medium rounded-lg hover:bg-amber-200 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Carrier
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Carrier name"
        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
        autoFocus
      />
      <button
        onClick={handleSave}
        disabled={saving || !name.trim()}
        className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      </button>
      <button
        onClick={() => { setOpen(false); setName(''); }}
        className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const CarrierCard = ({ carrier, onUpdate, expanded }) => {
  return (
    <div className={`${expanded ? 'px-6 py-4' : 'p-3 bg-gray-50 rounded-lg'} flex items-center justify-between`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
          <Building2 className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <p className="font-medium text-gray-900">{carrier.name}</p>
          <p className="text-sm text-gray-500">
            {carrier.mapd_payment_style === 'advance' ? 'Advance' : 'As-Earned'} • MAPD/PDP
          </p>
        </div>
      </div>
      {!carrier.is_active && (
        <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">Inactive</span>
      )}
    </div>
  );
};

const AddMedsupButton = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ carrierName: '', initialPercentage: '', renewalPercentage: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.initialPercentage || !form.renewalPercentage) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/commissions/medsup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setOpen(false);
        setForm({ carrierName: '', initialPercentage: '', renewalPercentage: '' });
        onAdd();
      }
    } catch (error) {
      console.error('Error adding medsup config:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-700 font-medium rounded-lg hover:bg-amber-200 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Rate
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={form.carrierName}
        onChange={(e) => setForm({ ...form, carrierName: e.target.value })}
        placeholder="Carrier"
        className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
      <input
        type="number"
        value={form.initialPercentage}
        onChange={(e) => setForm({ ...form, initialPercentage: e.target.value })}
        placeholder="Initial %"
        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
      <input
        type="number"
        value={form.renewalPercentage}
        onChange={(e) => setForm({ ...form, renewalPercentage: e.target.value })}
        placeholder="Renewal %"
        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
      <button onClick={handleSave} disabled={saving} className="p-2 bg-green-500 text-white rounded-lg">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      </button>
      <button onClick={() => setOpen(false)} className="p-2 bg-gray-100 text-gray-600 rounded-lg">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const MedsupConfigCard = ({ config, onUpdate }) => (
  <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
    <div>
      <p className="font-medium text-gray-900">{config.carrier_name || config.linked_carrier_name || 'Unknown'}</p>
      <p className="text-sm text-gray-500">
        {config.initial_percentage}% initial / {config.renewal_percentage}% renewal
      </p>
    </div>
  </div>
);

const AddAncillaryButton = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ productType: '', carrierName: '', initialRate: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.productType || !form.initialRate) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/commissions/ancillary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setOpen(false);
        setForm({ productType: '', carrierName: '', initialRate: '' });
        onAdd();
      }
    } catch (error) {
      console.error('Error adding ancillary config:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-700 font-medium rounded-lg hover:bg-amber-200 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Product
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <select
        value={form.productType}
        onChange={(e) => setForm({ ...form, productType: e.target.value })}
        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
      >
        <option value="">Select type</option>
        <option value="Dental">Dental</option>
        <option value="Vision">Vision</option>
        <option value="Hospital Indemnity">Hospital Indemnity</option>
        <option value="Cancer">Cancer</option>
        <option value="Heart/Stroke">Heart/Stroke</option>
        <option value="Life Insurance">Life Insurance</option>
        <option value="Other">Other</option>
      </select>
      <input
        type="text"
        value={form.carrierName}
        onChange={(e) => setForm({ ...form, carrierName: e.target.value })}
        placeholder="Carrier"
        className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
      <input
        type="number"
        value={form.initialRate}
        onChange={(e) => setForm({ ...form, initialRate: e.target.value })}
        placeholder="Rate %"
        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
      <button onClick={handleSave} disabled={saving} className="p-2 bg-green-500 text-white rounded-lg">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      </button>
      <button onClick={() => setOpen(false)} className="p-2 bg-gray-100 text-gray-600 rounded-lg">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const AncillaryConfigCard = ({ config, onUpdate }) => (
  <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
    <div>
      <p className="font-medium text-gray-900">{config.product_type}</p>
      <p className="text-sm text-gray-500">
        {config.carrier_name || 'Any carrier'} • {config.initial_rate}%
        {config.renewal_rate && ` / ${config.renewal_rate}% renewal`}
      </p>
    </div>
  </div>
);

const StatementDetailModal = ({ statement, onClose, onUpdate }) => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCarrier, setEditingCarrier] = useState(false);
  const [carrierInput, setCarrierInput] = useState('');
  const [carrierSuggestions, setCarrierSuggestions] = useState([]);
  const [currentCarrier, setCurrentCarrier] = useState(
    statement.detected_carrier || statement.ai_detected_carrier || ''
  );

  useEffect(() => {
    fetchDetails();
    fetchCarrierSuggestions();
  }, [statement.id]);

  const fetchCarrierSuggestions = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/dashboard/carrier-coverage?year=${new Date().getFullYear()}&month=${new Date().getMonth() + 1}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) setCarrierSuggestions(data.carriers?.map(c => c.name) || []);
    } catch (e) { /* ignore */ }
  };

  const handleAssignCarrier = async () => {
    if (!carrierInput.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/commissions/statements/${statement.id}/carrier`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ carrierName: carrierInput.trim() })
      });
      if (res.ok) {
        setCurrentCarrier(carrierInput.trim());
        setEditingCarrier(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error assigning carrier:', error);
    }
  };

  const fetchDetails = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/statements/${statement.id}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        setLines(data.lines || []);
      }
    } catch (error) {
      console.error('Error fetching statement details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/statements/${statement.id}/process`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Error processing statement:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this statement and all its data? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE}/commissions/statements/${statement.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Error deleting statement:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{statement.original_filename}</h3>
            <p className="text-sm text-gray-500">
              {statement.total_rows} rows • {statement.matched_count || 0} matched • {statement.unmatched_count || 0} unmatched
            </p>
            <div className="flex items-center gap-2 mt-1">
              {editingCarrier ? (
                <>
                  <input
                    type="text"
                    value={carrierInput}
                    onChange={(e) => setCarrierInput(e.target.value)}
                    list="carrier-suggestions"
                    placeholder="Enter carrier name"
                    className="text-sm border border-gray-300 rounded px-2 py-1 w-48 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleAssignCarrier()}
                  />
                  <datalist id="carrier-suggestions">
                    {carrierSuggestions.map((s, i) => <option key={i} value={s} />)}
                  </datalist>
                  <button onClick={handleAssignCarrier} className="p-1 text-green-600 hover:bg-green-50 rounded">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditingCarrier(false)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : currentCarrier ? (
                <>
                  <span className="text-sm font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                    <Building2 className="w-3 h-3 inline mr-1" />{currentCarrier}
                  </span>
                  <button
                    onClick={() => { setCarrierInput(currentCarrier); setEditingCarrier(true); }}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setCarrierInput(''); setEditingCarrier(true); }}
                  className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" /> No carrier — Assign
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={statement.status} />
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <LoadingState />
          ) : lines.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No line items found</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Policy #</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-center">Match</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map(line => (
                  <tr key={line.id}>
                    <td className="px-3 py-2">{line.member_name || `${line.member_first_name} ${line.member_last_name}`}</td>
                    <td className="px-3 py-2 text-gray-500">{line.policy_number || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(line.commission_amount)}</td>
                    <td className="px-3 py-2 text-center">
                      {line.matched_client_id ? (
                        <span className="text-green-600 font-medium">{line.matchedClientName}</span>
                      ) : (
                        <span className="text-gray-400">Unmatched</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleDelete}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              Close
            </button>
            {statement.status === 'matched' && (
              <button
                onClick={handleProcess}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg"
              >
                Process to Ledger
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const NewDisputeModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '',
    disputeType: 'missing_payment',
    description: '',
    disputedAmount: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/commissions/disputes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        onCreated();
      }
    } catch (error) {
      console.error('Error creating dispute:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">New Commission Dispute</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="Brief description of the issue"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dispute Type</label>
            <select
              value={form.disputeType}
              onChange={(e) => setForm({ ...form, disputeType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="missing_payment">Missing Payment</option>
              <option value="underpayment">Underpayment</option>
              <option value="incorrect_chargeback">Incorrect Chargeback</option>
              <option value="rate_discrepancy">Rate Discrepancy</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Disputed Amount</label>
            <input
              type="number"
              step="0.01"
              value={form.disputedAmount}
              onChange={(e) => setForm({ ...form, disputedAmount: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="$0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="Provide details about this dispute..."
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.title}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Dispute'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Commissions;
